// ./src/utils/analytics/providers/GoogleAnalyticsProvider.ts
//
// GOOGLE ANALYTICS 4 — the ONE place gtag.js is loaded and configured (ROADMAP #62 P7).
//
// ⚠⚠ Before P7 it was initialised THREE times — a hard-coded snippet in index.html (with the build's one tag, so the
// PartyLabz domain reported into the Eventsmagic property), `ReactGA.initialize` in App.tsx, and this provider's
// `setUser` re-running `config` — and each `config` sends its own page view, on top of `usePageTracking`'s. Now:
//   - the tag is the BRAND's (`appEnv.googleTag`, resolved from the hostname), loaded here and nowhere else;
//   - consent defaults are set BEFORE `config` (Consent Mode v2 — see `consent.ts`);
//   - `config` sends NO page view: `usePageTracking` sends exactly one per route;
//   - every hit carries the SANITISED url (`safePageLocation`), never one holding a `?token=` or `?inv=` secret — and the
//     sanitised REFERRER too: the crew link (`/crew-access?inv=…`) hard-navigates into the app, so the browser's own
//     `document.referrer` on the next page holds the secret, and gtag.js sends it as `page_referrer` unless told;
//   - a browser that said DECLINE sends nothing at all: GA's own opt-out switch (`ga-disable-<id>`) stops the tag's
//     automatic events too, and the app's calls are dropped before they reach it. ⓘ Consent Mode alone would still send
//     cookieless pings for it — right for a visitor who has not answered (Google's default), not for one who refused;
//   - a script an ad blocker refused makes "send, then leave" return at once, not after its one-second wait.
// ⚠ The data stream's Enhanced measurement → "Page changes based on browser history events" must be OFF in GA, or it
//   sends a second page view per route — with the raw URL. (Owner step, ROADMAP P7.)
import { AnalyticsProvider, AnalyticsEventParams } from '../types';
import { appliedConsent, CONSENT_REGIONS, privacySignalOn, readStoredConsent, subscribeConsent } from '../consent';
import { safePageLocation } from '../privacy';

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js';

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  name = 'google-analytics';
  private trackingId: string;
  private debugMode: boolean;
  private initialized = false;
  /** This browser answered Decline — nothing is sent (see the file header). */
  private declined = false;
  /** gtag.js failed to load (an ad blocker): a hit will never be handed over. */
  private blocked = false;
  /** The last page_location sent — the next page view's referrer (the browser's own one only for the first). */
  private lastLocation: string | null = null;

  constructor(trackingId: string, options?: { debugMode?: boolean }) {
    this.trackingId = trackingId;
    this.debugMode = options?.debugMode ?? false;
  }

  initialize(): void {
    if (this.initialized || !this.trackingId || typeof window === 'undefined') {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    // ⚠⚠ gtag.js reads each queued command as an ARGUMENTS object. The tempting `(...args) => dataLayer.push(args)`
    //   pushes an ARRAY, which gtag.js silently ignores — every hit lost, no error anywhere.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // Consent first: a hit queued before the defaults would go out without them. ⓘ The region line wins over the
    // global one for those countries (Google applies the most specific default).
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'granted',
    });
    window.gtag('consent', 'default', { analytics_storage: 'denied', region: [...CONSENT_REGIONS] });

    // The visitor's answer, else a browser's Global Privacy Control declines.
    const stored = appliedConsent(readStoredConsent(), privacySignalOn());

    this.setDeclined(stored === 'denied');

    if (stored) {
      window.gtag('consent', 'update', { analytics_storage: stored });
    }

    subscribeConsent((consent) => {
      this.setDeclined(consent === 'denied');
      window.gtag('consent', 'update', { analytics_storage: consent });
    });

    window.gtag('js', new Date());
    window.gtag('config', this.trackingId, {
      send_page_view: false,
      page_location: safePageLocation(window.location.href),
      page_referrer: safePageLocation(document.referrer),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...(this.debugMode ? { debug_mode: true } : {}),
    });

    const script = document.createElement('script');

    script.async = true;
    script.src = `${GTAG_SRC}?id=${encodeURIComponent(this.trackingId)}`;
    script.onerror = () => {
      this.blocked = true;
    };
    document.head.appendChild(script);

    this.initialized = true;
  }

  /** GA's documented opt-out: while `window['ga-disable-<id>']` is true the tag sends nothing, its own events included. */
  private setDeclined(declined: boolean): void {
    this.declined = declined;
    (window as unknown as Record<string, unknown>)[`ga-disable-${this.trackingId}`] = declined;
  }

  pageView(pageUrl: string, pageTitle: string, routePattern?: string | null): void {
    if (!this.initialized || this.declined) {
      return;
    }

    const pageLocation = safePageLocation(pageUrl);
    const pageReferrer = this.lastLocation ?? safePageLocation(document.referrer);
    // ⓘ The ROUTE ("/events/:eventId/guests") as GA's content group: urls hold record ids, so every event's guest list
    //   is a different page_location and a report by page shatters into one row per event.
    const page = {
      page_location: pageLocation,
      page_referrer: pageReferrer,
      page_title: pageTitle,
      ...(routePattern ? { content_group: routePattern } : {}),
    };

    this.lastLocation = pageLocation;

    // ⓘ `set` first, so every later event on this route carries the same sanitised url, title and group.
    window.gtag('set', page);
    window.gtag('event', 'page_view', page);
  }

  sendEvent(eventName: string, params?: AnalyticsEventParams): void {
    if (!this.initialized || this.declined) {
      return;
    }

    window.gtag('event', eventName, params ?? {});
  }

  sendEventThen(eventName: string, params: AnalyticsEventParams | undefined, done: () => void): void {
    if (!this.initialized || this.declined || this.blocked) {
      done();

      return;
    }

    // ⓘ No `transport_type` — a Universal Analytics field; GA4's gtag.js already sends by beacon / keepalive fetch.
    window.gtag('event', eventName, { ...(params ?? {}), event_callback: done });
  }

  setUser(userId: string | null): void {
    if (!this.initialized) {
      return;
    }

    // ⚠ `set`, never `config` — a second `config` re-initialises the tag and sends another page view.
    window.gtag('set', { user_id: userId });
  }
}
